import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../../services/api';
import { isPanchangMockEnabled } from '../utils/featureFlags';
import { getMockPanchangaNow } from './panchangaNowMock';

const ENDPOINT = '/panchang/now';

function buildParams(lat, lng) {
  const params = {};
  if (typeof lat === 'number' && Number.isFinite(lat)) params.lat = lat;
  if (typeof lng === 'number' && Number.isFinite(lng)) params.lng = lng;
  return params;
}

export async function fetchPanchangaOnce(lat, lng) {
  if (isPanchangMockEnabled()) return getMockPanchangaNow();
  try {
    const response = await api.get(ENDPOINT, { params: buildParams(lat, lng) });
    const payload = response && response.data;
    if (!payload) return null;
    return payload.data || payload;
  } catch (err) {
    return null;
  }
}

export function usePanchangaNow({ frozen = false, snapshot, lat, lng } = {}) {
  const [data, setData] = useState(frozen && snapshot ? snapshot : null);
  const [loading, setLoading] = useState(!frozen);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (isPanchangMockEnabled()) {
      setData(getMockPanchangaNow());
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(ENDPOINT, { params: buildParams(lat, lng) });
      if (!mountedRef.current) return;
      const payload = response && response.data;
      const next = payload ? payload.data || payload : null;
      setData(next);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setData(null);
      setError(err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [lat, lng]);

  useEffect(() => {
    mountedRef.current = true;
    if (frozen) {
      setData(snapshot || null);
      setLoading(false);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozen, load]);

  return { data, loading, error, isLive: !frozen };
}
